import { useEffect, useState } from 'react';
import { Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { sendIndividualMessage, useLeadsFetcher } from '@/features/leads/api/use-leads';
import { usePermissionPassbooks } from '@/features/permissions/api/use-permissions';
import { usePassbookQuery } from '@/shared/lib/passbook';
import type { Passbook } from '@/shared/types';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

const EMPTY_PASSBOOK = {} as Passbook;

/**
 * Manda o texto do push como mensagem individual (`POST /registration/message`)
 * para um lead de uma loja, achado pelo CPF. Sem rota nova no servidor.
 */
export function PushTestForm({ message }: { message: string }) {
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [store, setStore] = useState<{ id: string; name: string } | null>(null);
  const [cpf, setCpf] = useState('');
  const [sending, setSending] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const { data, isFetching } = usePermissionPassbooks(term, 1);
  const options = (data?.rows ?? []).slice(0, 6);
  const { data: passbook } = usePassbookQuery(store?.id ?? '', { enabled: Boolean(store) });
  const fetchLeads = useLeadsFetcher(store?.id ?? '', passbook ?? EMPTY_PASSBOOK);
  const digits = cpf.replace(/\D/g, '');
  const ready = Boolean(store && passbook) && digits.length >= 5 && message.trim().length > 0;

  const send = async () => {
    if (!store || !passbook) return;
    setSending(true);
    try {
      const result = await fetchLeads({ pageIndex: 0, pageSize: 5, sorting: [], search: digits });
      const lead =
        result.rows.find((row) => (row.profile.uniqueId ?? '').replace(/\D/g, '') === digits) ?? result.rows[0];
      if (!lead?.serialNumber) {
        toast.error('Lead não encontrado nessa loja.');
        return;
      }
      await sendIndividualMessage(store.id, {
        serialNumber: lead.serialNumber,
        uniqueId: lead.profile.uniqueId ?? digits,
        email: lead.email,
        phone: lead.phone,
        description: message,
      });
      toast.success(`Push de teste enviado para ${lead.profile.name || digits}.`, {
        description: 'Entra no histórico de mensagens individuais do lead.',
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Não foi possível enviar o teste.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3 rounded-xl border border-border/60 p-3">
      <div>
        <p className="text-sm font-medium">Enviar teste para um lead</p>
        <p className="text-xs text-muted-foreground">Vai como mensagem individual, só o texto, e entra no histórico do lead.</p>
      </div>
      {store ? (
        <div className="flex items-center gap-2 rounded-lg bg-muted/30 px-3 py-1.5 text-sm">
          <span className="min-w-0 flex-1 truncate">{store.name}</span>
          <Button type="button" variant="ghost" size="icon-sm" aria-label="Trocar loja" onClick={() => setStore(null)}>
            <X />
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="teste-push-loja" className="sr-only">
            Loja
          </Label>
          <Input
            id="teste-push-loja"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar loja"
            autoComplete="off"
          />
          {term ? (
            <ul className="divide-y divide-border/60 rounded-xl border border-border/60">
              {options.length === 0 ? (
                <li className="px-3 py-2 text-xs text-muted-foreground">{isFetching ? 'Buscando…' : 'Nenhuma loja encontrada.'}</li>
              ) : (
                options.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      className="flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-accent/50"
                      onClick={() => setStore({ id: row.id, name: row.name })}
                    >
                      <span className="truncate">{row.name}</span>
                      <span className="truncate text-xs text-muted-foreground">{row.clientName}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          ) : null}
        </div>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          className="min-w-0 flex-1"
          value={cpf}
          onChange={(event) => setCpf(event.target.value)}
          placeholder="CPF do lead"
          inputMode="numeric"
          autoComplete="off"
          aria-label="CPF do lead"
        />
        <Button type="button" variant="secondary" onClick={() => void send()} loading={sending} disabled={!ready}>
          <Send /> Enviar teste
        </Button>
      </div>
    </div>
  );
}
